/**
 * P2-WORKSPACE D1-slice — design versioning + override provenance (headless).
 *
 * The vertical slice's "version -> approve" spine, built headless-first so the
 * UI wires into typed, tested contracts (P0 conventions: contracts before UI).
 *
 * Binding rules (P2 card deliverable 5 + Phase 0 contracts):
 * - **Every override captured**: author, timestamp, previous value, reason,
 *   resulting version — the full provenance tuple, nothing less.
 * - **Version history is append-only** (Decision 002 Q4 semantics): edits
 *   create versions; originals survive; current state is derived (last version).
 * - **Approve is a human act** with an actor recorded; approval is itself a
 *   versioned event.
 * - **Factory-owned fields are never overridable from Skafer** (fail closed,
 *   handoff-contract.md field ownership).
 * - Deterministic where inputs are deterministic; timestamps/actors are
 *   caller-provided (testable, no hidden clock).
 */
import { isFactoryOwnedField, isSkaferOwnedField } from './schemas';

export const DESIGN_VERSIONING_VERSION = '0.1';

/** The design state a version snapshots — the fields the engine recommends. */
export interface DesignState {
  /** Production OrderForm vocabulary (handoff-contract.md §4) so overrides
   *  map 1:1 to the eventual handoff. Length in cm at this layer; mm fields
   *  carry production names. */
  skiType: string;
  length: number;                       // cm
  noseWidth: number;                    // mm
  tailWidth: number;                    // mm
  waistWidthRecommendation: number;     // mm
  flexPattern: string;
  noseBlockId?: number;
  tailBlockId?: number;
}

export type OverrideField = keyof DesignState;

export interface OverrideRecord {
  field: OverrideField;
  previousValue: number | string;
  newValue: number | string;
  reason: string;
  by: string;
  at: string; // caller-provided ISO timestamp (no hidden clock)
}

export interface DesignVersion {
  version: number;
  status: 'draft' | 'approved';
  state: DesignState;
  /** Overrides applied ON TOP of the previous version to create this one. */
  overrides: OverrideRecord[];
  /** Where the base of this version came from (D3 provenance). */
  precedentRef?: { serial?: number; orderFormId?: number };
  approvedBy?: string;
  approvedAt?: string;
}

export interface DesignVersionLog {
  versions: DesignVersion[];
  /** Derived current state = last version (Decision 002 Q4). */
  current: DesignVersion | null;
}

export function emptyDesignLog(): DesignVersionLog {
  return { versions: [], current: null };
}

function assertOverridable(field: OverrideField): void {
  if (isFactoryOwnedField(field)) {
    throw new Error(`field ${field} is factory-owned — Skafer may never override it`);
  }
  if (!isSkaferOwnedField(field)) {
    throw new Error(`field ${field} is not a known Skafer-owned design field`);
  }
}

/**
 * Create version 1 from a recommendation output (the engine's D3 result).
 * The engine proposes; this records it as an unsigned draft.
 */
export function startDesignVersion(
  state: DesignState,
  precedentRef?: { serial?: number; orderFormId?: number },
): DesignVersionLog {
  const v: DesignVersion = {
    version: 1,
    status: 'draft',
    state: { ...state },
    overrides: [],
    precedentRef,
  };
  return { versions: [v], current: v };
}

/** Apply ONE override on top of the current version -> a new draft version. */
export function applyOverride(
  log: DesignVersionLog,
  override: OverrideRecord,
): DesignVersionLog {
  if (!log.current) throw new Error('no current version to override');
  assertOverridable(override.field);
  if (!override.reason || !override.reason.trim()) {
    throw new Error('override requires a reason (provenance is a product feature)');
  }
  const prev = log.current.state[override.field];
  if (prev === undefined || prev === override.newValue) {
    throw new Error(`override of ${override.field} is a no-op (value unchanged or missing)`);
  }
  const nextState: DesignState = { ...log.current.state };
  (nextState as Record<OverrideField, number | string>)[override.field] = override.newValue;
  const v: DesignVersion = {
    version: log.current.version + 1,
    status: 'draft',
    state: nextState,
    overrides: [{ ...override, previousValue: prev }],
  };
  return { versions: [...log.versions, v], current: v };
}

/** Designer approves the current version -> it becomes the approved version. */
export function approveCurrent(
  log: DesignVersionLog,
  by: string,
  at: string,
): DesignVersionLog {
  if (!log.current) throw new Error('no current version to approve');
  if (log.current.status === 'approved') {
    throw new Error(`version ${log.current.version} is already approved`);
  }
  const v: DesignVersion = {
    ...log.current,
    status: 'approved',
    approvedBy: by,
    approvedAt: at,
  };
  const versions = [...log.versions];
  versions[versions.length - 1] = v;
  return { versions, current: v };
}

/** Field-level audit trail: every override across all versions, in order. */
export function overrideTrail(log: DesignVersionLog): Array<OverrideRecord & { version: number }> {
  return log.versions.flatMap((v) =>
    v.overrides.map((o) => ({ ...o, version: v.version })),
  );
}